import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { refreshToken } from "@api/auth";
import { store } from "@store";
import { useAuthStore } from "@/plugins/2.pinia/modules/auth";
import { router } from "@plugins/1.router";
import { cookies, setCookie, removeCookie } from "@/plugins/useCookies";

function createApiInstance(baseURL, addRequestId = false) {
  const instance = axios.create({ baseURL });

  instance.interceptors.request.use(
    async function (config) {
      const authStore = useAuthStore(store);

      const now = Date.now();
      const shouldRefresh =
        authStore.tokenExpiresAt && now >= authStore.tokenExpiresAt;

      if (shouldRefresh && !refreshingToken) {
        refreshingToken = true;
        try {
          await refreshToken();
          refreshingToken = false;
          requestsQueue.forEach((cb) => cb());
          requestsQueue = [];
        } catch (err) {
          refreshingToken = false;
          await authStore.logout();
          removeCookie(`NEXUS-${import.meta.env.MODE}-accessToken`);
          // await router.push("/");
          return Promise.reject(err);
        }
      }

      if (config.useAuthToken === false) {
        delete config.headers.Authorization;
      } else if (authStore.accessToken) {
        config.headers["Authorization"] = "Bearer " + authStore.accessToken;
      }

      if (addRequestId) {
        config.headers["X-Request-Id"] = uuidv4();
      }

      delete config.useAuthToken;
      return config;
    },
    function (error) {
      return Promise.reject(error);
    },
  );

  let refreshingToken = false;
  let requestsQueue = [];

  instance.interceptors.response.use(
    (response) => {
      refreshingToken = false;
      return response;
    },
    async (error) => {
      const authStore = useAuthStore(store);

      const originalRequest = error.config;

      console.log("Axios error:", error.response);

      if (error.response?.status === 401) {
        if (!refreshingToken) {
          refreshingToken = true;
          try {
            await refreshToken();
            const updatedToken = authStore.accessToken;
            if (!updatedToken) throw new Error("Token refresh failed");

            originalRequest.headers["Authorization"] = "Bearer " + updatedToken;
            const response = await instance.request(originalRequest);

            requestsQueue.forEach((callback) => callback());
            requestsQueue = [];

            return response;
          } catch (refreshError) {
            refreshingToken = false;
            requestsQueue = [];
            await authStore.logout();
            removeCookie(`NEXUS-${import.meta.env.MODE}-accessToken`);
            // await router.push("/");
            return Promise.reject(refreshError);
          }
        }

        return new Promise((resolve, reject) => {
          requestsQueue.push(() => {
            originalRequest.headers["Authorization"] =
              "Bearer " + authStore.accessToken;
            instance.request(originalRequest).then(resolve).catch(reject);
          });
        });
      }

      return Promise.reject(error);
    },
  );

  return instance;
}

const $apiV1 = createApiInstance(import.meta.env.VITE_API_V1_URL);
const $apiV5 = createApiInstance(import.meta.env.VITE_API_V5_URL, true);
const $api = createApiInstance(import.meta.env.VITE_API_URL);

export { $apiV1, $apiV5, $api };
